import React, {
  useCallback,
  useMemo,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useRebateForecast } from 'src/api/rebates/useRebateForecast';
import useRebateAgreementYears from 'src/api/rebates/useRebateAgreementYears';
import { MultiSelectOption } from 'src/components/common/MultiSelect';
import { SingleSelect } from 'src/components/common/SingleSelect';
import useDivisions from 'src/api/organization/useDivisions';
import useSuppliersByIds from 'src/api/accounts-payable/useSuppliersByIds';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import {
  useResetForecast,
  useResetForecastAndReported,
} from '@/api/rebates/useResetForecast';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import {
  createSupplierIdToNameMap,
  createDivisionIdToNameMap,
  normalizeDaysFromStartForLeapYear,
} from 'src/utils/rebateForecastingUtils';
import { useRuleTagFiltering } from 'src/hooks/useRuleTagFiltering';
import { useAgreementTagFiltering } from 'src/hooks/useAgreementTagFiltering';
import { useSupplierTagFiltering } from 'src/hooks/useSupplierTagFiltering';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import {
  RebateBreakdown,
  RebatePoint,
  REBATES_SYSTEM_ENUMS,
} from '@erp/shared-types';
import { useForecastDataPipeline } from '@/components/rebates/hooks/useForecastDataPipeline';
import { useFilteredForecastData } from '@/components/rebates/hooks/useFilteredForecastData';
import { useCumulativeForecastData } from '@/components/rebates/hooks/useCumulativeForecastData';
import { useSplitPoint } from '@/components/rebates/hooks/useSplitPoint';
import ForecastCard, {
  PREDICTION_ALGORITHMS,
  PredictionAlgorithm,
  ALL_PREDICTION_ALGORITHMS,
} from '@/components/rebates/ForecastCard';

import { ForecastControlsFAB } from '@/components/rebates/ForecastControlsFAB';
import type { FilterGroup } from '@/components/rebates/FilterPanelContent';
import { RebateAnalyticsTables } from '@/components/rebates/RebateAnalyticsTables';
import {
  TableConfig,
  generateTableLabel,
  MultiYearForecastData,
  DimensionType,
  LineType,
} from '@/types/rebateAnalytics';
import { generateTableName } from '@/utils/rebateAnalyticsTableUtils';
import {
  useCustomViews,
  useCreateCustomView,
  useUpdateCustomView,
  useDeleteCustomView,
  useReorderCustomViews,
} from '@/api/rpc/hooks/core/useCustomViews';
import useCurrentUser from '@/api/auth/useCurrentUser';
import {
  ForegroundAgentChat,
  BreakdownTablePayload,
  FilterUpdate,
} from '@/components/common/ForegroundAgentChat';

// Search params type for the rebates analytics route
type RebatesAnalyticsSearchParams = {
  cumulative?: boolean;
  ruleTagIds?: string[];
  ruleType?: Array<'conditional' | 'non-conditional'>;
  agreementTagIds?: string[];
  supplierTagIds?: string[];
  agreementIds?: string[];
  supplierIds?: string[];
  divisionIds?: string[];
  year?: number;
  visibleLines?: string[];
  primaryLine?: string;
};

const RebatesAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();

  // Get URL search params
  const searchParams = useSearch({
    strict: false,
  }) as RebatesAnalyticsSearchParams;

  // Get current user for creator name
  const { data: currentUserData } = useCurrentUser();
  const creatorName = currentUserData?.user?.name;

  // Year filter state - default to current year (single year only)
  const currentYear = new Date().getFullYear();

  // State for cumulative vs month-by-month toggle (shared between chart and breakdowns)
  // Initialize from URL or default to true
  const [isCumulative, setIsCumulative] = React.useState(
    searchParams.cumulative ?? true,
  );
  // Year filter state - initialize from URL or default to current year
  const [selectedYear, setSelectedYear] = useState<number | null>(
    searchParams.year ?? currentYear,
  );

  // Determine if we're viewing a past year
  const isPastYear = selectedYear !== null && selectedYear < currentYear;

  // Note: Prediction algorithm visibility is now controlled via visibleLines
  // Each algorithm (linear-regression, ai-forecasting, etc.) is directly in visibleLines

  // State for line visibility and primary selection
  // Initialize from URL params, or default based on year
  const [visibleLines, setVisibleLines] = React.useState<Set<string>>(() => {
    // If URL has visibleLines, use those
    if (searchParams.visibleLines && searchParams.visibleLines.length > 0) {
      return new Set(searchParams.visibleLines);
    }
    // Otherwise, default based on year
    const year = searchParams.year ?? currentYear;
    if (year < currentYear) {
      return new Set(['estimates', 'previousYear', 'reported']);
    }
    // Include all prediction algorithms by default instead of 'predictions'
    return new Set([
      'estimates',
      ...ALL_PREDICTION_ALGORITHMS,
      'reported',
      'previousYear',
    ]);
  });
  const [primaryLine, setPrimaryLine] = React.useState<LineType>(() => {
    // If URL has primaryLine, use it
    if (searchParams.primaryLine) {
      return searchParams.primaryLine as LineType;
    }
    // Otherwise, default based on year
    const year = searchParams.year ?? currentYear;
    if (year < currentYear) {
      return 'estimates';
    }
    return 'sarima';
  });

  // Admin check
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);

  // Bulk forecast reset state and mutations (admin only)
  const [resetForecastDialogOpen, setResetForecastDialogOpen] = useState(false);
  const [resetWithReportedDialogOpen, setResetWithReportedDialogOpen] =
    useState(false);
  const bulkResetForecastMutation = useResetForecast();
  const bulkResetWithReportedMutation = useResetForecastAndReported();

  // Fetch custom table configurations from API
  const { data: customViewsData, isLoading: customViewsLoading } =
    useCustomViews({
      module: 'REBATES',
      section: 'ANALYTICS_TABLES',
    });

  // Transform API views to TableConfig format
  const tableConfigs: TableConfig[] = useMemo(() => {
    if (!customViewsData?.views) return [];
    return customViewsData.views.map((view) => {
      const configData = view.config as Omit<
        TableConfig,
        'id' | 'label' | 'creatorName' | 'creatorId'
      >;
      return {
        id: view.id,
        label: view.title,
        creatorName: view.creatorName || undefined,
        creatorId: view.creatorId || undefined,
        ...configData,
      };
    });
  }, [customViewsData]);

  // Mutations
  const createCustomView = useCreateCustomView();
  const updateCustomView = useUpdateCustomView();
  const deleteCustomView = useDeleteCustomView();
  const reorderCustomViews = useReorderCustomViews();

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Calculate date range for forecast (single year)
  const selectedYearForQuery = selectedYear ?? currentYear;
  const forecastStartDate = `${selectedYearForQuery}-01-01`;
  const forecastEndDate = `${selectedYearForQuery}-12-31`;

  // Calculate previous year for YOY comparison
  const previousYear = selectedYearForQuery - 1;
  const previousYearStartDate = `${previousYear}-01-01`;
  const previousYearEndDate = `${previousYear}-12-31`;

  // Fetch forecast data for current year
  const {
    data: forecastData,
    isLoading,
    error,
  } = useRebateForecast({
    startDate: forecastStartDate,
    endDate: forecastEndDate,
  });

  // Fetch forecast data for previous year in parallel
  // Only fetch estimates and historical - we don't need predictions/reported for YOY comparison
  const {
    data: previousYearForecastData,
    isLoading: isLoadingPreviousYear,
    error: previousYearError,
  } = useRebateForecast({
    startDate: previousYearStartDate,
    endDate: previousYearEndDate,
    calculationTypes: ['estimate', 'historical'],
  });

  // Normalize previous year's daysFromStart to account for leap year differences
  // This ensures proper alignment when comparing years (e.g., 2024 leap year vs 2025 non-leap year)
  const normalizedPreviousYearData = useMemo(() => {
    if (!previousYearForecastData) return null;

    // Check if years have different leap year status
    const isCurrentYearLeap =
      new Date(selectedYearForQuery, 1, 29).getDate() === 29;
    const isPreviousYearLeap = new Date(previousYear, 1, 29).getDate() === 29;

    // If leap year status differs, adjust daysFromStart for dates after Feb 28
    if (isCurrentYearLeap !== isPreviousYearLeap) {
      // If previous year was a leap year, it has one extra day, so subtract 1 from its daysFromStart
      // If current year is a leap year, previous year is missing a day, so add 1 to its daysFromStart
      const offset = isPreviousYearLeap ? -1 : 1;
      return normalizeDaysFromStartForLeapYear(
        previousYearForecastData,
        offset,
      );
    }

    return previousYearForecastData;
  }, [previousYearForecastData, selectedYearForQuery, previousYear]);

  // Create MultiYearForecastData structure early (used by many hooks)
  const multiYearForecastData: MultiYearForecastData = useMemo(() => {
    return {
      currentYear: selectedYearForQuery,
      currentYearData: forecastData ?? null,
      previousYearData: normalizedPreviousYearData ?? null,
      isCumulative,
    };
  }, [
    selectedYearForQuery,
    forecastData,
    normalizedPreviousYearData,
    isCumulative,
  ]);

  // Get all agreement IDs from current forecast data (for bulk reset)
  const agreementIds = useMemo(() => {
    if (!multiYearForecastData.currentYearData?.agreements) return [];
    return multiYearForecastData.currentYearData.agreements
      .map((a) => a.id)
      .filter((id): id is string => !!id);
  }, [multiYearForecastData.currentYearData?.agreements]);

  // Handle bulk forecast reset (admin only)
  const handleBulkResetForecast = async () => {
    if (agreementIds.length === 0) return;

    try {
      await bulkResetForecastMutation.mutateAsync({ agreementIds });
      setResetForecastDialogOpen(false);
    } catch (error) {
      console.error('Error resetting forecast:', error);
    }
  };

  // Handle bulk forecast reset with reported wipe (admin only)
  const handleBulkResetForecastAndReported = async () => {
    if (agreementIds.length === 0) return;

    try {
      await bulkResetWithReportedMutation.mutateAsync({ agreementIds });
      setResetWithReportedDialogOpen(false);
    } catch (error) {
      console.error('Error resetting forecast and reported:', error);
    }
  };

  // Fetch divisions data for proper name mapping
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Derive supplier IDs from forecast to batch fetch exact names
  const supplierIdsFromForecast = useMemo(() => {
    if (!multiYearForecastData.currentYearData) return [] as string[];
    const fromMap = createSupplierIdToNameMap(
      multiYearForecastData.currentYearData,
    );
    return Array.from(fromMap.keys());
  }, [multiYearForecastData.currentYearData]);

  // Batch fetch suppliers by IDs (authoritative mapping)
  const { data: suppliersByIds } = useSuppliersByIds(
    supplierIdsFromForecast,
    !!supplierIdsFromForecast.length,
  );

  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];

    // Gather all division IDs actually used in forecast data (estimates + predictions)
    const usedDivisionIds = new Set<string>();

    if (multiYearForecastData.currentYearData) {
      // Collect from estimates
      multiYearForecastData.currentYearData.estimates?.forEach((rp) => {
        rp.breakdowns?.forEach((bd) => {
          if (bd.divisionId) usedDivisionIds.add(bd.divisionId);
        });
      });

      // Collect from predictions
      Object.values(
        multiYearForecastData.currentYearData.predictions || {},
      ).forEach((points) => {
        points?.forEach((rp) => {
          rp.breakdowns?.forEach((bd) => {
            if (bd.divisionId) usedDivisionIds.add(bd.divisionId);
          });
        });
      });
    }

    // Map only divisions that appear in the forecast
    return divisionsData.divisions
      .filter(
        (division) =>
          usedDivisionIds.size === 0 || usedDivisionIds.has(division.id),
      )
      .map((division) => ({
        id: division.id,
        label: division.name,
        value: division,
        group: division.tags?.[0]?.name,
      }));
  }, [divisionsData?.divisions, multiYearForecastData.currentYearData]);
  // Division tag selection
  const [selectedDivisionTags, setSelectedDivisionTags] = React.useState<
    MultiSelectOption[]
  >([]);

  // Ref to track if we're syncing from URL to avoid infinite loops
  const isSyncingFromURL = useRef(false);

  // Ref to track the previous year to only sync lines when year actually changes
  const previousYearRef = useRef<number | null | undefined>(undefined);

  // Default visible lines for comparison (to avoid storing defaults in URL)
  const defaultVisibleLinesForYear = useCallback(
    (year: number) => {
      if (year < currentYear) {
        return new Set(['estimates', 'previousYear', 'reported']);
      }
      // Include all prediction algorithms by default
      return new Set([
        'estimates',
        ...ALL_PREDICTION_ALGORITHMS,
        'reported',
        'previousYear',
      ]);
    },
    [currentYear],
  );

  const defaultPrimaryLineForYear = useCallback(
    (year: number) => {
      if (year < currentYear) {
        return 'estimates';
      }
      return 'sarima';
    },
    [currentYear],
  );

  // Helper to update URL search params
  const updateURLSearchParams = useCallback(
    (updates: {
      cumulative?: boolean;
      ruleTagIds?: string[];
      ruleType?: Array<'conditional' | 'non-conditional'>;
      agreementTagIds?: string[];
      supplierTagIds?: string[];
      agreementIds?: string[];
      supplierIds?: string[];
      divisionIds?: string[];
      year?: number;
      visibleLines?: string[];
      primaryLine?: string;
    }) => {
      // Save current scroll position
      const scrollY = window.scrollY;

      // Calculate default visible lines for the current/updated year
      const effectiveYear = updates.year ?? searchParams.year ?? currentYear;
      const defaultLines = defaultVisibleLinesForYear(effectiveYear);
      const defaultPrimary = defaultPrimaryLineForYear(effectiveYear);

      // Helper to check if visible lines match defaults
      const visibleLinesMatchDefault = (lines: string[]) => {
        if (lines.length !== defaultLines.size) return false;
        return lines.every((l) => defaultLines.has(l));
      };

      navigate({
        to: '/rebates/analytics',
        replace: true, // Replace current history entry instead of adding new one
        search: {
          cumulative:
            updates.cumulative !== undefined
              ? updates.cumulative === true
                ? undefined
                : updates.cumulative
              : searchParams.cumulative,
          ruleTagIds:
            updates.ruleTagIds !== undefined
              ? updates.ruleTagIds.length > 0
                ? updates.ruleTagIds
                : undefined
              : searchParams.ruleTagIds,
          ruleType:
            updates.ruleType !== undefined
              ? updates.ruleType.length === 2
                ? undefined
                : updates.ruleType.length > 0
                  ? updates.ruleType
                  : undefined
              : searchParams.ruleType,
          agreementTagIds:
            updates.agreementTagIds !== undefined
              ? updates.agreementTagIds.length > 0
                ? updates.agreementTagIds
                : undefined
              : searchParams.agreementTagIds,
          supplierTagIds:
            updates.supplierTagIds !== undefined
              ? updates.supplierTagIds.length > 0
                ? updates.supplierTagIds
                : undefined
              : searchParams.supplierTagIds,
          agreementIds:
            updates.agreementIds !== undefined
              ? updates.agreementIds.length > 0
                ? updates.agreementIds
                : undefined
              : searchParams.agreementIds,
          supplierIds:
            updates.supplierIds !== undefined
              ? updates.supplierIds.length > 0
                ? updates.supplierIds
                : undefined
              : searchParams.supplierIds,
          divisionIds:
            updates.divisionIds !== undefined
              ? updates.divisionIds.length > 0
                ? updates.divisionIds
                : undefined
              : searchParams.divisionIds,
          year:
            updates.year !== undefined
              ? updates.year === currentYear
                ? undefined
                : updates.year
              : searchParams.year,
          // Only store visibleLines in URL if they differ from defaults
          visibleLines:
            updates.visibleLines !== undefined
              ? visibleLinesMatchDefault(updates.visibleLines)
                ? undefined
                : updates.visibleLines
              : searchParams.visibleLines,
          // Only store primaryLine in URL if it differs from default
          primaryLine:
            updates.primaryLine !== undefined
              ? updates.primaryLine === defaultPrimary
                ? undefined
                : updates.primaryLine
              : searchParams.primaryLine,
        } as RebatesAnalyticsSearchParams,
      }).then(() => {
        // Restore scroll position after navigation completes
        // Use requestAnimationFrame to ensure it happens after render
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      });
    },
    [
      navigate,
      searchParams,
      currentYear,
      defaultVisibleLinesForYear,
      defaultPrimaryLineForYear,
    ],
  );

  const handleDivisionTagsChange = useCallback(
    (selectedTags: MultiSelectOption[]) => {
      if (isSyncingFromURL.current) {
        setSelectedDivisionTags(selectedTags);
        return;
      }
      setSelectedDivisionTags(selectedTags);
      updateURLSearchParams({
        divisionIds: selectedTags.map((d) => d.id),
      });
    },
    [updateURLSearchParams],
  );

  const agreementOptions: MultiSelectOption[] = useMemo(() => {
    if (!multiYearForecastData.currentYearData) return [];

    const uniqueAgreements = new Map<string, string>();

    // From agreements array (primary metadata source - has names and tags)
    multiYearForecastData.currentYearData.agreements?.forEach((agreement) => {
      if (agreement.id && agreement.name) {
        uniqueAgreements.set(agreement.id, agreement.name);
      }
    });

    // From breakdowns (secondary / fallback)
    const addFromBreakdowns = (points: RebatePoint[]) => {
      points.forEach((point) => {
        point.breakdowns?.forEach((bd) => {
          if (bd.agreementId && bd.agreementName) {
            uniqueAgreements.set(bd.agreementId, bd.agreementName);
          }
        });
      });
    };

    multiYearForecastData.currentYearData.estimates &&
      addFromBreakdowns(multiYearForecastData.currentYearData.estimates);
    Object.values(
      multiYearForecastData.currentYearData.predictions || {},
    ).forEach(addFromBreakdowns);

    return Array.from(uniqueAgreements.entries()).map(([id, name]) => ({
      id,
      label: name,
    }));
  }, [multiYearForecastData.currentYearData]);

  const [selectedAgreements, setSelectedAgreements] = React.useState<
    MultiSelectOption[]
  >([]);
  const handleAgreementsChange = useCallback(
    (selectedAgreements: MultiSelectOption[]) => {
      if (isSyncingFromURL.current) {
        setSelectedAgreements(selectedAgreements);
        return;
      }
      setSelectedAgreements(selectedAgreements);
      updateURLSearchParams({
        agreementIds: selectedAgreements.map((a) => a.id),
      });
    },
    [updateURLSearchParams],
  );

  const supplierOptions: MultiSelectOption[] = useMemo(() => {
    if (!multiYearForecastData.currentYearData) return [];

    const uniqueSuppliers = new Map<string, string>();

    // From suppliers array (primary metadata source - has names and tags)
    multiYearForecastData.currentYearData.suppliers?.forEach((supplier) => {
      if (supplier.id && supplier.name) {
        uniqueSuppliers.set(supplier.id, supplier.name);
      }
    });

    // From breakdowns (secondary / fallback)
    const addFromBreakdowns = (points: RebatePoint[]) => {
      points.forEach((point) => {
        point.breakdowns?.forEach((bd) => {
          if (bd.supplierId && bd.supplierName) {
            uniqueSuppliers.set(bd.supplierId, bd.supplierName);
          }
        });
      });
    };

    multiYearForecastData.currentYearData.estimates &&
      addFromBreakdowns(multiYearForecastData.currentYearData.estimates);
    Object.values(
      multiYearForecastData.currentYearData.predictions || {},
    ).forEach(addFromBreakdowns);

    return Array.from(uniqueSuppliers.entries()).map(([id, name]) => ({
      id,
      label: name,
    }));
  }, [multiYearForecastData.currentYearData]);

  const [selectedSuppliers, setSelectedSuppliers] = React.useState<
    MultiSelectOption[]
  >([]);
  const handleSuppliersChange = useCallback(
    (selectedSuppliers: MultiSelectOption[]) => {
      if (isSyncingFromURL.current) {
        setSelectedSuppliers(selectedSuppliers);
        return;
      }
      setSelectedSuppliers(selectedSuppliers);
      updateURLSearchParams({
        supplierIds: selectedSuppliers.map((s) => s.id),
      });
    },
    [updateURLSearchParams],
  );

  // Create maps for supplier and division names (cached for performance)
  const { supplierIdToName, divisionIdToName } = useMemo(() => {
    const supplierIdToName = new Map<string, string>();
    const divisionIdToName = new Map<string, string>();

    // Supplier names: prefer batch-by-ids response
    if (suppliersByIds) {
      suppliersByIds.forEach((s) => supplierIdToName.set(s.id, s.name));
    }

    // Division names: combine divisions API and forecast as fallback
    if (divisionsData?.divisions) {
      divisionsData.divisions.forEach((division) => {
        divisionIdToName.set(division.id, division.name);
      });
    }
    if (multiYearForecastData.currentYearData) {
      const forecastDivisionMap = createDivisionIdToNameMap(
        multiYearForecastData.currentYearData,
      );
      forecastDivisionMap.forEach((name, id) => {
        if (!divisionIdToName.has(id)) {
          divisionIdToName.set(id, name);
        }
      });
    }

    return { supplierIdToName, divisionIdToName };
  }, [suppliersByIds, divisionsData, multiYearForecastData.currentYearData]);

  // Rule tag filtering
  const {
    selectedTagIds: selectedRuleTagIds,
    setSelectedTagIds: setSelectedRuleTagIds,
    availableTagIds: availableRuleTagIds,
    filteredRuleIds,
    ruleTagsMap,
  } = useRuleTagFiltering(multiYearForecastData.currentYearData?.rules);

  // State for conditional/non-conditional selections (default to both selected)
  const [selectedConditionalTypes, setSelectedConditionalTypes] =
    React.useState<Set<'conditional' | 'non-conditional'>>(
      new Set(['conditional', 'non-conditional']),
    );

  // Create rule conditional mapping: ruleId -> isConditional
  const ruleIdToIsConditional = useMemo(() => {
    const map = new Map<string, boolean>();
    if (multiYearForecastData.currentYearData?.rules) {
      multiYearForecastData.currentYearData.rules.forEach((rule) => {
        map.set(rule.id, rule.isConditional);
      });
    }
    return map;
  }, [multiYearForecastData.currentYearData?.rules]);

  // Agreement tag filtering
  const {
    selectedTagIds: selectedAgreementTagIds,
    setSelectedTagIds: setSelectedAgreementTagIds,
    availableTagIds: availableAgreementTagIds,
    filteredAgreementIds,
    agreementTagsMap,
  } = useAgreementTagFiltering(
    multiYearForecastData.currentYearData?.agreements,
  );

  // Supplier tag filtering
  const {
    selectedTagIds: selectedSupplierTagIds,
    setSelectedTagIds: setSelectedSupplierTagIds,
    availableTagIds: availableSupplierTagIds,
    filteredSupplierIds,
    supplierTagsMap,
  } = useSupplierTagFiltering(multiYearForecastData.currentYearData?.suppliers);

  // Get available tag names for the filter UI
  const { data: rebateRuleTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.RULE_TAGS.name,
    includeOptions: true,
  });
  const { data: rebateAgreementTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.AGREEMENT_TAGS.name,
    includeOptions: true,
  });
  const { data: supplierTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.SUPPLIER_TAGS.name,
    includeOptions: true,
  });

  const availableRuleTags = useMemo(() => {
    if (!rebateRuleTagsEnum?.edges[0]?.options) return [];
    // Show all tags from the enum, not just those associated with current forecast data
    return rebateRuleTagsEnum.edges[0].options;
  }, [rebateRuleTagsEnum?.edges]);

  const availableAgreementTags = useMemo(() => {
    if (!rebateAgreementTagsEnum?.edges[0]?.options) return [];
    // Show all tags from the enum, not just those associated with current forecast data
    return rebateAgreementTagsEnum.edges[0].options;
  }, [rebateAgreementTagsEnum?.edges]);

  const availableSupplierTags = useMemo(() => {
    if (!supplierTagsEnum?.edges[0]?.options) return [];
    // Show all tags from the enum, not just those associated with current forecast data
    return supplierTagsEnum.edges[0].options;
  }, [supplierTagsEnum?.edges]);

  // Create tag ID to name maps (shared with tables)
  const ruleTagIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (rebateRuleTagsEnum?.edges[0]?.options) {
      rebateRuleTagsEnum.edges[0].options.forEach((tag) => {
        map.set(tag.id, tag.name || 'Unnamed Tag');
      });
    }
    return map;
  }, [rebateRuleTagsEnum?.edges]);

  const agreementTagIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (rebateAgreementTagsEnum?.edges[0]?.options) {
      rebateAgreementTagsEnum.edges[0].options.forEach((tag) => {
        map.set(tag.id, tag.name || 'Unnamed Tag');
      });
    }
    return map;
  }, [rebateAgreementTagsEnum?.edges]);

  const supplierTagIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (supplierTagsEnum?.edges[0]?.options) {
      supplierTagsEnum.edges[0].options.forEach((tag) => {
        map.set(tag.id, tag.name || 'Unnamed Tag');
      });
    }
    return map;
  }, [supplierTagsEnum?.edges]);

  // Convert ruleTagsMap from tag IDs to tag names for ForecastCard
  const ruleTagsMapWithNames = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!ruleTagsMap || !ruleTagIdToName) return map;

    Object.entries(ruleTagsMap).forEach(([ruleId, tagIds]) => {
      const tagNames = tagIds
        .map((tagId) => ruleTagIdToName.get(tagId))
        .filter((name): name is string => !!name);
      if (tagNames.length > 0) {
        map.set(ruleId, tagNames);
      }
    });

    return map;
  }, [ruleTagsMap, ruleTagIdToName]);

  // Create tag name to ID map for ForecastCard
  const tagNameToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    if (rebateRuleTagsEnum?.edges[0]?.options) {
      rebateRuleTagsEnum.edges[0].options.forEach((tag) => {
        if (tag.name) {
          map.set(tag.name, tag.id);
        }
      });
    }
    return map;
  }, [rebateRuleTagsEnum?.edges]);

  // Create rule and agreement ID to name maps from forecast data (shared with tables)
  const ruleIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (multiYearForecastData.currentYearData?.rules) {
      multiYearForecastData.currentYearData.rules.forEach((rule) => {
        if (rule.id && rule.name) {
          map.set(rule.id, rule.name);
        }
      });
    }
    return map;
  }, [multiYearForecastData.currentYearData?.rules]);

  const agreementIdToName = useMemo(() => {
    const map = new Map<string, string>();
    // Use agreements array from forecast data (has names and tags)
    if (multiYearForecastData.currentYearData?.agreements) {
      multiYearForecastData.currentYearData.agreements.forEach((agreement) => {
        if (agreement.id && agreement.name) {
          map.set(agreement.id, agreement.name);
        }
      });
    }
    // Fallback: extract from breakdowns if agreements array not available
    if (map.size === 0 && multiYearForecastData.currentYearData) {
      const addFromBreakdowns = (points: RebatePoint[]) => {
        points.forEach((point) => {
          point.breakdowns?.forEach((bd) => {
            if (
              bd.agreementId &&
              bd.agreementName &&
              !map.has(bd.agreementId)
            ) {
              map.set(bd.agreementId, bd.agreementName);
            }
          });
        });
      };
      multiYearForecastData.currentYearData.estimates &&
        addFromBreakdowns(multiYearForecastData.currentYearData.estimates);
      Object.values(
        multiYearForecastData.currentYearData.predictions || {},
      ).forEach(addFromBreakdowns);
    }
    return map;
  }, [multiYearForecastData.currentYearData]);

  // Convert tags to MultiSelectOption format
  const ruleTagOptions: MultiSelectOption[] = useMemo(() => {
    return availableRuleTags.map((tag) => ({
      id: tag.id,
      label: tag.name || 'Unnamed Tag',
    }));
  }, [availableRuleTags]);

  // Rule Type options (conditional vs non-conditional)
  const ruleTypeOptions: MultiSelectOption[] = useMemo(() => {
    return [
      { id: '__conditional__', label: 'Conditional' },
      { id: '__non_conditional__', label: 'Non-Conditional' },
    ];
  }, []);

  const agreementTagOptions: MultiSelectOption[] = useMemo(() => {
    return availableAgreementTags.map((tag) => ({
      id: tag.id,
      label: tag.name || 'Unnamed Tag',
    }));
  }, [availableAgreementTags]);

  const supplierTagOptions: MultiSelectOption[] = useMemo(() => {
    return availableSupplierTags.map((tag) => ({
      id: tag.id,
      label: tag.name || 'Unnamed Tag',
    }));
  }, [availableSupplierTags]);

  // Convert selected tag IDs to MultiSelectOption format
  const selectedRuleTags: MultiSelectOption[] = useMemo(() => {
    return ruleTagOptions.filter((option) => selectedRuleTagIds.has(option.id));
  }, [ruleTagOptions, selectedRuleTagIds]);

  // Convert selected conditional types to MultiSelectOption format
  const selectedRuleTypes: MultiSelectOption[] = useMemo(() => {
    const options: MultiSelectOption[] = [];
    if (selectedConditionalTypes.has('conditional')) {
      options.push({ id: '__conditional__', label: 'Conditional' });
    }
    if (selectedConditionalTypes.has('non-conditional')) {
      options.push({ id: '__non_conditional__', label: 'Non-Conditional' });
    }
    return options;
  }, [selectedConditionalTypes]);

  const selectedAgreementTags: MultiSelectOption[] = useMemo(() => {
    return agreementTagOptions.filter((option) =>
      selectedAgreementTagIds.has(option.id),
    );
  }, [agreementTagOptions, selectedAgreementTagIds]);

  const selectedSupplierTags: MultiSelectOption[] = useMemo(() => {
    return supplierTagOptions.filter((option) =>
      selectedSupplierTagIds.has(option.id),
    );
  }, [supplierTagOptions, selectedSupplierTagIds]);

  // Sync URL params to filter state on mount/URL change
  useEffect(() => {
    if (isSyncingFromURL.current) return;
    isSyncingFromURL.current = true;

    // Sync cumulative
    if (searchParams.cumulative !== undefined) {
      setIsCumulative(searchParams.cumulative);
    }

    // Sync year
    if (searchParams.year !== undefined) {
      setSelectedYear(searchParams.year);
      // Only reset visible lines and primary line when year actually changes
      // AND there are no explicit URL params for these values
      const yearActuallyChanged = previousYearRef.current !== searchParams.year;
      if (yearActuallyChanged) {
        previousYearRef.current = searchParams.year;
        // Only apply defaults if visibleLines/primaryLine are NOT in the URL
        // (URL params take precedence over year-based defaults)
        if (!searchParams.visibleLines && !searchParams.primaryLine) {
          // When syncing to a past year, default to estimates only
          if (searchParams.year < currentYear) {
            setVisibleLines(new Set(['estimates', 'previousYear', 'reported']));
            setPrimaryLine('estimates');
          } else if (searchParams.year === currentYear) {
            setVisibleLines(
              new Set([
                'estimates',
                ...ALL_PREDICTION_ALGORITHMS,
                'reported',
                'previousYear',
              ]),
            );
            setPrimaryLine('sarima');
          }
        }
      }
    }

    // Sync visibleLines from URL
    if (searchParams.visibleLines !== undefined) {
      const urlLines = new Set(searchParams.visibleLines);
      const currentLinesArray = Array.from(visibleLines);
      const urlLinesArray = Array.from(urlLines);
      if (
        currentLinesArray.length !== urlLinesArray.length ||
        currentLinesArray.some((line) => !urlLines.has(line))
      ) {
        setVisibleLines(urlLines);
      }
    }

    // Sync primaryLine from URL
    if (searchParams.primaryLine !== undefined) {
      if (primaryLine !== searchParams.primaryLine) {
        setPrimaryLine(searchParams.primaryLine as LineType);
      }
    }

    // Sync rule tag IDs
    if (searchParams.ruleTagIds !== undefined) {
      const urlTagIds = new Set(searchParams.ruleTagIds);
      const currentTagIds = selectedRuleTagIds;
      if (
        currentTagIds.size !== urlTagIds.size ||
        Array.from(currentTagIds).some((id) => !urlTagIds.has(id))
      ) {
        setSelectedRuleTagIds(urlTagIds);
      }
    }

    // Sync rule type
    if (searchParams.ruleType !== undefined) {
      const urlTypes = new Set(searchParams.ruleType);
      const currentTypes = selectedConditionalTypes;
      if (
        currentTypes.size !== urlTypes.size ||
        Array.from(currentTypes).some((type) => !urlTypes.has(type))
      ) {
        setSelectedConditionalTypes(urlTypes);
      }
    } else if (
      searchParams.ruleType === undefined &&
      selectedConditionalTypes.size !== 2
    ) {
      // Default to both if not in URL
      setSelectedConditionalTypes(new Set(['conditional', 'non-conditional']));
    }

    // Sync agreement tag IDs
    if (searchParams.agreementTagIds !== undefined) {
      const urlTagIds = new Set(searchParams.agreementTagIds);
      const currentTagIds = selectedAgreementTagIds;
      if (
        currentTagIds.size !== urlTagIds.size ||
        Array.from(currentTagIds).some((id) => !urlTagIds.has(id))
      ) {
        setSelectedAgreementTagIds(urlTagIds);
      }
    }

    // Sync supplier tag IDs
    if (searchParams.supplierTagIds !== undefined) {
      const urlTagIds = new Set(searchParams.supplierTagIds);
      const currentTagIds = selectedSupplierTagIds;
      if (
        currentTagIds.size !== urlTagIds.size ||
        Array.from(currentTagIds).some((id) => !urlTagIds.has(id))
      ) {
        setSelectedSupplierTagIds(urlTagIds);
      }
    }

    // Sync agreement IDs
    if (searchParams.agreementIds !== undefined) {
      const urlIds = new Set(searchParams.agreementIds);
      const currentIds = new Set(selectedAgreements.map((a) => a.id));
      if (
        currentIds.size !== urlIds.size ||
        Array.from(currentIds).some((id) => !urlIds.has(id))
      ) {
        const selected = agreementOptions.filter((opt) => urlIds.has(opt.id));
        setSelectedAgreements(selected);
      }
    }

    // Sync supplier IDs
    if (searchParams.supplierIds !== undefined) {
      const urlIds = new Set(searchParams.supplierIds);
      const currentIds = new Set(selectedSuppliers.map((s) => s.id));
      if (
        currentIds.size !== urlIds.size ||
        Array.from(currentIds).some((id) => !urlIds.has(id))
      ) {
        const selected = supplierOptions.filter((opt) => urlIds.has(opt.id));
        setSelectedSuppliers(selected);
      }
    }

    // Sync division IDs
    if (searchParams.divisionIds !== undefined) {
      const urlIds = new Set(searchParams.divisionIds);
      const currentIds = new Set(selectedDivisionTags.map((d) => d.id));
      if (
        currentIds.size !== urlIds.size ||
        Array.from(currentIds).some((id) => !urlIds.has(id))
      ) {
        const selected = divisionOptions.filter((opt) => urlIds.has(opt.id));
        setSelectedDivisionTags(selected);
      }
    }

    isSyncingFromURL.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchParams.cumulative,
    searchParams.year,
    searchParams.ruleTagIds,
    searchParams.ruleType,
    searchParams.agreementTagIds,
    searchParams.supplierTagIds,
    searchParams.agreementIds,
    searchParams.supplierIds,
    searchParams.divisionIds,
    searchParams.visibleLines,
    searchParams.primaryLine,
    agreementOptions,
    supplierOptions,
    divisionOptions,
  ]);

  // Wrap setIsCumulative to also update URL
  const handleCumulativeChange = useCallback(
    (value: boolean) => {
      if (isSyncingFromURL.current) {
        setIsCumulative(value);
        return;
      }
      setIsCumulative(value);
      updateURLSearchParams({ cumulative: value });
    },
    [updateURLSearchParams],
  );

  // Wrap setSelectedYear to also update URL
  const handleYearChange = useCallback(
    (year: number | null) => {
      if (isSyncingFromURL.current) {
        setSelectedYear(year);
        return;
      }
      setSelectedYear(year);

      // Update the ref to track this year change (prevents useEffect from re-triggering line reset)
      previousYearRef.current = year ?? currentYear;

      // When switching years, reset to default lines for that year
      // and clear visibleLines/primaryLine from URL (so defaults apply)
      const effectiveYear = year ?? currentYear;
      const newVisibleLines = defaultVisibleLinesForYear(effectiveYear);
      const newPrimaryLine = defaultPrimaryLineForYear(effectiveYear);

      setVisibleLines(newVisibleLines);
      setPrimaryLine(newPrimaryLine as LineType);

      // Update URL with year and clear line settings (they'll be omitted as they match defaults)
      updateURLSearchParams({
        year: effectiveYear,
        visibleLines: Array.from(newVisibleLines),
        primaryLine: newPrimaryLine,
      });
    },
    [
      updateURLSearchParams,
      currentYear,
      defaultVisibleLinesForYear,
      defaultPrimaryLineForYear,
    ],
  );

  // MultiSelect change handlers
  const handleRuleTagsChange = useCallback(
    (selectedTags: MultiSelectOption[]) => {
      if (isSyncingFromURL.current) {
        const newTagIds = new Set(selectedTags.map((tag) => tag.id));
        setSelectedRuleTagIds(newTagIds);
        return;
      }
      const newTagIds = new Set(selectedTags.map((tag) => tag.id));
      setSelectedRuleTagIds(newTagIds);
      updateURLSearchParams({ ruleTagIds: Array.from(newTagIds) });
    },
    [setSelectedRuleTagIds, updateURLSearchParams],
  );

  const handleRuleTypesChange = useCallback(
    (selectedTypes: MultiSelectOption[]) => {
      const conditionalTypes = new Set<'conditional' | 'non-conditional'>();
      selectedTypes.forEach((option) => {
        if (option.id === '__conditional__') {
          conditionalTypes.add('conditional');
        } else if (option.id === '__non_conditional__') {
          conditionalTypes.add('non-conditional');
        }
      });
      if (isSyncingFromURL.current) {
        setSelectedConditionalTypes(conditionalTypes);
        return;
      }
      setSelectedConditionalTypes(conditionalTypes);
      updateURLSearchParams({ ruleType: Array.from(conditionalTypes) });
    },
    [updateURLSearchParams],
  );

  const handleAgreementTagsChange = useCallback(
    (selectedTags: MultiSelectOption[]) => {
      const newTagIds = new Set(selectedTags.map((tag) => tag.id));
      if (isSyncingFromURL.current) {
        setSelectedAgreementTagIds(newTagIds);
        return;
      }
      setSelectedAgreementTagIds(newTagIds);
      updateURLSearchParams({ agreementTagIds: Array.from(newTagIds) });
    },
    [setSelectedAgreementTagIds, updateURLSearchParams],
  );

  const handleSupplierTagsChange = useCallback(
    (selectedTags: MultiSelectOption[]) => {
      const newTagIds = new Set(selectedTags.map((tag) => tag.id));
      if (isSyncingFromURL.current) {
        setSelectedSupplierTagIds(newTagIds);
        return;
      }
      setSelectedSupplierTagIds(newTagIds);
      updateURLSearchParams({ supplierTagIds: Array.from(newTagIds) });
    },
    [setSelectedSupplierTagIds, updateURLSearchParams],
  );
  const selectedDivisionIdSet = useMemo(
    () => new Set(selectedDivisionTags.map((t) => t.id)),
    [selectedDivisionTags],
  );

  const selectedSupplierIdSet = useMemo(
    () => new Set(selectedSuppliers.map((s) => s.id)),
    [selectedSuppliers],
  );

  const selectedAgreementIdSet = useMemo(
    () => new Set(selectedAgreements.map((a) => a.id)),
    [selectedAgreements],
  );

  const activeFilterNames = useMemo(() => {
    const names: string[] = [];
    if (selectedRuleTagIds.size > 0) names.push('Rule Tags');
    if (selectedConditionalTypes.size < 2) names.push('Rule Type');
    if (selectedAgreementTagIds.size > 0) names.push('Agreement Tags');
    if (selectedSupplierTagIds.size > 0) names.push('Supplier Tags');
    if (selectedDivisionTags.length > 0) names.push('Divisions');
    if (selectedSuppliers.length > 0) names.push('Suppliers');
    if (selectedAgreements.length > 0) names.push('Agreements');
    return names;
  }, [
    selectedRuleTagIds.size,
    selectedConditionalTypes.size,
    selectedAgreementTagIds.size,
    selectedSupplierTagIds.size,
    selectedDivisionTags.length,
    selectedSuppliers.length,
    selectedAgreements.length,
  ]);

  // Active filter count for the FAB
  const activeFilterCount = useMemo(() => {
    return (
      selectedRuleTagIds.size +
      (selectedConditionalTypes.size < 2 ? 1 : 0) +
      selectedAgreementTagIds.size +
      selectedSupplierTagIds.size +
      selectedDivisionTags.length +
      selectedSuppliers.length +
      selectedAgreements.length
    );
  }, [
    selectedRuleTagIds.size,
    selectedConditionalTypes.size,
    selectedAgreementTagIds.size,
    selectedSupplierTagIds.size,
    selectedDivisionTags.length,
    selectedSuppliers.length,
    selectedAgreements.length,
  ]);

  // Clear all filters handler for FAB
  const handleClearAllFilters = React.useCallback(() => {
    setSelectedRuleTagIds(new Set());
    setSelectedConditionalTypes(new Set(['conditional', 'non-conditional']));
    setSelectedAgreementTagIds(new Set());
    setSelectedSupplierTagIds(new Set());
    setSelectedDivisionTags([]);
    setSelectedSuppliers([]);
    setSelectedAgreements([]);
    updateURLSearchParams({
      ruleTagIds: [],
      ruleType: [],
      agreementTagIds: [],
      supplierTagIds: [],
      agreementIds: [],
      supplierIds: [],
      divisionIds: [],
    });
  }, [
    setSelectedRuleTagIds,
    setSelectedAgreementTagIds,
    setSelectedSupplierTagIds,
    updateURLSearchParams,
  ]);

  // Handler for visible lines changes that also updates URL
  const handleVisibleLinesChange = useCallback(
    (newLines: Set<string>) => {
      if (isSyncingFromURL.current) {
        setVisibleLines(newLines);
        return;
      }
      setVisibleLines(newLines);
      updateURLSearchParams({ visibleLines: Array.from(newLines) });
    },
    [updateURLSearchParams],
  );

  // Handler for primary line changes that also updates URL
  const handlePrimaryLineChange = useCallback(
    (newPrimaryLine: string) => {
      if (isSyncingFromURL.current) {
        setPrimaryLine(newPrimaryLine as LineType);
        return;
      }
      setPrimaryLine(newPrimaryLine as LineType);
      updateURLSearchParams({ primaryLine: newPrimaryLine });
    },
    [updateURLSearchParams],
  );

  // Reset all controls to defaults handler for FAB
  const handleResetAll = React.useCallback(() => {
    // Get default values for current year
    const effectiveYear = selectedYear ?? currentYear;
    const defaultLines = defaultVisibleLinesForYear(effectiveYear);
    const defaultPrimary = defaultPrimaryLineForYear(effectiveYear);

    // Reset cumulative toggle to true
    setIsCumulative(true);
    // Reset visible lines to defaults for this year
    setVisibleLines(defaultLines);
    // Reset primary line to default for this year
    setPrimaryLine(defaultPrimary as LineType);

    // Clear all filter states directly (not just URL)
    setSelectedRuleTagIds(new Set());
    setSelectedConditionalTypes(new Set(['conditional', 'non-conditional']));
    setSelectedAgreementTagIds(new Set());
    setSelectedSupplierTagIds(new Set());
    setSelectedDivisionTags([]);
    setSelectedSuppliers([]);
    setSelectedAgreements([]);

    // Update URL to clear all params
    updateURLSearchParams({
      ruleTagIds: [],
      ruleType: [],
      agreementTagIds: [],
      supplierTagIds: [],
      agreementIds: [],
      supplierIds: [],
      divisionIds: [],
      visibleLines: Array.from(defaultLines),
      primaryLine: defaultPrimary,
      cumulative: true,
    });
  }, [
    selectedYear,
    currentYear,
    defaultVisibleLinesForYear,
    defaultPrimaryLineForYear,
    updateURLSearchParams,
    setSelectedRuleTagIds,
    setSelectedAgreementTagIds,
    setSelectedSupplierTagIds,
  ]);

  // Handler for AI-driven filter updates
  const handleAIFilterUpdate = useCallback(
    (filters: FilterUpdate) => {
      if (filters.clearAllFilters) {
        handleResetAll();
        return;
      }

      if (filters.cumulative !== undefined) {
        handleCumulativeChange(filters.cumulative);
      }

      if (filters.visibleLines !== undefined) {
        // Expand 'predictions' shorthand to all prediction algorithms
        const expandedLines = filters.visibleLines.flatMap((line) =>
          line === 'predictions' ? ALL_PREDICTION_ALGORITHMS : [line],
        );

        // Enforce gating: if any prediction algorithm is in the list, ensure estimates is also included
        const hasPredictionAlgorithm = expandedLines.some((line) =>
          ALL_PREDICTION_ALGORITHMS.includes(line as PredictionAlgorithm),
        );
        const hasEstimates = expandedLines.includes('estimates');

        if (hasPredictionAlgorithm && !hasEstimates) {
          expandedLines.unshift('estimates');
        }

        handleVisibleLinesChange(new Set(expandedLines));
      }

      if (filters.primaryLine !== undefined) {
        handlePrimaryLineChange(filters.primaryLine);
      }

      // Handle division filtering by name
      if (filters.divisionNames !== undefined) {
        if (filters.divisionNames.length === 0) {
          // Clear division filters
          handleDivisionTagsChange([]);
        } else {
          // Find divisions by name and select them
          const matchingDivisions = divisionOptions.filter((opt) =>
            filters.divisionNames!.some(
              (name) =>
                opt.label.toLowerCase().includes(name.toLowerCase()) ||
                name.toLowerCase().includes(opt.label.toLowerCase()),
            ),
          );
          handleDivisionTagsChange(matchingDivisions);
        }
      }

      // Handle supplier filtering by name
      if (filters.supplierNames !== undefined) {
        if (filters.supplierNames.length === 0) {
          // Clear supplier filters
          handleSuppliersChange([]);
        } else {
          // Find suppliers by name and select them
          const matchingSuppliers = supplierOptions.filter((opt) =>
            filters.supplierNames!.some(
              (name) =>
                opt.label.toLowerCase().includes(name.toLowerCase()) ||
                name.toLowerCase().includes(opt.label.toLowerCase()),
            ),
          );
          handleSuppliersChange(matchingSuppliers);
        }
      }

      // Handle division exclusion by name (select all EXCEPT matching)
      if (
        filters.excludeDivisionNames !== undefined &&
        filters.excludeDivisionNames.length > 0
      ) {
        const excludedDivisions = divisionOptions.filter(
          (opt) =>
            !filters.excludeDivisionNames!.some(
              (name) =>
                opt.label.toLowerCase().includes(name.toLowerCase()) ||
                name.toLowerCase().includes(opt.label.toLowerCase()),
            ),
        );
        handleDivisionTagsChange(excludedDivisions);
      }

      // Handle supplier exclusion by name (select all EXCEPT matching)
      if (
        filters.excludeSupplierNames !== undefined &&
        filters.excludeSupplierNames.length > 0
      ) {
        const includedSuppliers = supplierOptions.filter(
          (opt) =>
            !filters.excludeSupplierNames!.some(
              (name) =>
                opt.label.toLowerCase().includes(name.toLowerCase()) ||
                name.toLowerCase().includes(opt.label.toLowerCase()),
            ),
        );
        handleSuppliersChange(includedSuppliers);
      }
    },
    [
      handleResetAll,
      handleCumulativeChange,
      handleVisibleLinesChange,
      handlePrimaryLineChange,
      handleDivisionTagsChange,
      handleSuppliersChange,
      divisionOptions,
      supplierOptions,
    ],
  );

  // Check if reset is available (anything changed from defaults)
  const isResetAvailable = React.useMemo(() => {
    // Check if cumulative is not default (default is true)
    if (!isCumulative) return true;

    // Get year-aware defaults
    const effectiveYear = selectedYear ?? currentYear;
    const defaultLines = defaultVisibleLinesForYear(effectiveYear);
    const defaultPrimary = defaultPrimaryLineForYear(effectiveYear);

    // Check if visible lines differ from year-based defaults
    if (
      visibleLines.size !== defaultLines.size ||
      !Array.from(defaultLines).every((line) => visibleLines.has(line))
    ) {
      return true;
    }
    // Check if primary line differs from year-based default
    if (primaryLine !== defaultPrimary) return true;
    // Check if any filters are active
    if (activeFilterCount > 0) return true;
    return false;
  }, [
    isCumulative,
    visibleLines,
    primaryLine,
    activeFilterCount,
    selectedYear,
    currentYear,
    defaultVisibleLinesForYear,
    defaultPrimaryLineForYear,
  ]);

  // Filter groups configuration for FAB
  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        label: REBATES_SYSTEM_ENUMS.RULE_TAGS.name,
        options: ruleTagOptions,
        value: selectedRuleTags,
        onChange: handleRuleTagsChange,
        placeholder: 'Select rule tags...',
        searchPlaceholder: 'Search rule tags...',
      },
      {
        label: 'Rule Type',
        options: ruleTypeOptions,
        value: selectedRuleTypes,
        onChange: handleRuleTypesChange,
        placeholder: 'Select rule types...',
        searchPlaceholder: 'Search rule types...',
        error: selectedConditionalTypes.size === 0,
        shouldAutoSort: false,
      },
      {
        label: REBATES_SYSTEM_ENUMS.AGREEMENT_TAGS.name,
        options: agreementTagOptions,
        value: selectedAgreementTags,
        onChange: handleAgreementTagsChange,
        placeholder: 'Select agreement tags...',
        searchPlaceholder: 'Search agreement tags...',
      },
      {
        label: REBATES_SYSTEM_ENUMS.SUPPLIER_TAGS.name,
        options: supplierTagOptions,
        value: selectedSupplierTags,
        onChange: handleSupplierTagsChange,
        placeholder: 'Select supplier tags...',
        searchPlaceholder: 'Search supplier tags...',
      },
      {
        label: 'Agreements',
        options: agreementOptions,
        value: selectedAgreements,
        onChange: handleAgreementsChange,
        placeholder: 'Select agreements...',
        searchPlaceholder: 'Search agreements...',
      },
      {
        label: 'Suppliers',
        options: supplierOptions,
        value: selectedSuppliers,
        onChange: handleSuppliersChange,
        placeholder: 'Select suppliers...',
        searchPlaceholder: 'Search suppliers...',
      },
      {
        label: 'Divisions',
        options: divisionOptions,
        value: selectedDivisionTags,
        onChange: handleDivisionTagsChange,
        placeholder: 'Select divisions...',
        searchPlaceholder: 'Search divisions...',
      },
    ],
    [
      ruleTagOptions,
      selectedRuleTags,
      handleRuleTagsChange,
      ruleTypeOptions,
      selectedRuleTypes,
      handleRuleTypesChange,
      selectedConditionalTypes.size,
      agreementTagOptions,
      selectedAgreementTags,
      handleAgreementTagsChange,
      supplierTagOptions,
      selectedSupplierTags,
      handleSupplierTagsChange,
      agreementOptions,
      selectedAgreements,
      handleAgreementsChange,
      supplierOptions,
      selectedSuppliers,
      handleSuppliersChange,
      divisionOptions,
      selectedDivisionTags,
      handleDivisionTagsChange,
    ],
  );

  const filterFns = useMemo(
    () => ({
      // Rule filter: TAGS via useRuleTagFiltering AND conditional/non-conditional filtering
      rule: (bd: RebateBreakdown) => {
        // Tag filtering: if tags selected, must match filteredRuleIds
        const tagFilterPasses =
          selectedRuleTagIds.size === 0 || filteredRuleIds.has(bd.ruleId);

        // Conditional/non-conditional filtering
        const isConditional = ruleIdToIsConditional.get(bd.ruleId) ?? false;
        const conditionalFilterPasses =
          (isConditional && selectedConditionalTypes.has('conditional')) ||
          (!isConditional && selectedConditionalTypes.has('non-conditional'));

        // Both filters must pass (AND logic)
        return tagFilterPasses && conditionalFilterPasses;
      },

      // Agreement filter: TAGS via useAgreementTagFiltering AND explicit agreement multi-select
      agreement: (bd: RebateBreakdown) => {
        // Tag filtering: if tags selected, must match filteredAgreementIds
        const tagFilterPasses =
          selectedAgreementTagIds.size === 0 ||
          filteredAgreementIds.has(bd.agreementId);
        // Explicit multi-select filtering
        const explicitFilterPasses =
          selectedAgreementIdSet.size === 0 ||
          selectedAgreementIdSet.has(bd.agreementId);
        return tagFilterPasses && explicitFilterPasses;
      },

      // Division filter uses the explicit picks
      division: (bd: RebateBreakdown) =>
        selectedDivisionIdSet.size === 0 ||
        selectedDivisionIdSet.has(bd.divisionId),

      // Supplier filter: TAGS via useSupplierTagFiltering AND explicit supplier multi-select
      supplier: (bd: RebateBreakdown) => {
        // Tag filtering: if tags selected, must match filteredSupplierIds
        const tagFilterPasses =
          selectedSupplierTagIds.size === 0 ||
          filteredSupplierIds.has(bd.supplierId);
        // Explicit multi-select filtering
        const explicitFilterPasses =
          selectedSupplierIdSet.size === 0 ||
          selectedSupplierIdSet.has(bd.supplierId);
        return tagFilterPasses && explicitFilterPasses;
      },
    }),
    [
      filteredRuleIds,
      filteredAgreementIds,
      filteredSupplierIds,
      selectedRuleTagIds,
      selectedAgreementTagIds,
      selectedSupplierTagIds,
      selectedAgreementIdSet,
      selectedDivisionIdSet,
      selectedSupplierIdSet,
      selectedConditionalTypes,
      ruleIdToIsConditional,
    ],
  );

  // Process current year data through pipeline: Filter → Merge → Cumulative
  const {
    transformed: transformedCurrentYearData,
    extended: extendedCurrentYearData,
  } = useForecastDataPipeline({
    rawData: multiYearForecastData.currentYearData ?? undefined,
    filterFns,
    isCumulative,
  });

  // Calculate split point for past/future predictions (needs extended data)
  const currentDate = useMemo(() => new Date(), []);
  const { lastRealEstimate, predictionSplitDay } = useSplitPoint(
    extendedCurrentYearData,
    currentDate,
  );

  // Previous year: Filter only (skip merge to keep estimates and predictions separate for YOY charts)
  // Then apply cumulative if needed
  const filteredPreviousYearData = useFilteredForecastData({
    forecastData: multiYearForecastData.previousYearData ?? undefined,
    filterFns,
  });

  // Apply cumulative to previous year data (without merge, no split point needed)
  const transformedPreviousYearData = useCumulativeForecastData(
    filteredPreviousYearData,
    isCumulative,
  );

  // Create transformed MultiYearForecastData
  const transformedForecastData: MultiYearForecastData = useMemo(() => {
    const result = {
      currentYear: selectedYearForQuery,
      currentYearData: transformedCurrentYearData ?? null,
      previousYearData: transformedPreviousYearData ?? null,
      isCumulative,
    };

    return result;
  }, [
    selectedYearForQuery,
    transformedCurrentYearData,
    transformedPreviousYearData,
    isCumulative,
  ]);

  // Get available prediction algorithms from transformed forecast data
  // This must use transformedCurrentYearData since that's what ForecastCard uses
  const availablePredictionAlgorithms = useMemo((): PredictionAlgorithm[] => {
    const predictions = transformedCurrentYearData?.predictions;
    if (!predictions) {
      return ['linear-regression'] as PredictionAlgorithm[];
    }
    const keys = Object.keys(predictions);
    // Filter to only include keys that are in PREDICTION_ALGORITHMS
    const algorithms = keys.filter(
      (key): key is PredictionAlgorithm => key in PREDICTION_ALGORITHMS,
    );
    // Fallback to linear-regression if no valid algorithms found
    return algorithms.length > 0
      ? algorithms
      : (['linear-regression'] as PredictionAlgorithm[]);
  }, [transformedCurrentYearData?.predictions]);

  // Handlers for custom table management
  const handleAddTable = useCallback(
    async (config: Omit<TableConfig, 'id' | 'label'>, label: string) => {
      await createCustomView.mutateAsync({
        module: 'REBATES',
        section: 'ANALYTICS_TABLES',
        config: config as Record<string, unknown>,
        title: label,
      });
    },
    [createCustomView],
  );

  // Handler for AI-created breakdown tables
  const handleAICreateBreakdownTable = useCallback(
    (payload: BreakdownTablePayload) => {
      const config: Omit<TableConfig, 'id' | 'label'> = {
        primaryXDimension:
          (payload.tableConfig.primaryXDimension as DimensionType) ||
          DimensionType.TIME,
        secondaryXDimension: payload.tableConfig
          .secondaryXDimension as DimensionType,
        primaryYDimension: payload.tableConfig
          .primaryYDimension as DimensionType,
        secondaryYDimension: payload.tableConfig
          .secondaryYDimension as DimensionType,
        // Always default to 'all' for AI-created tables, overriding AI's choice
        timeFilterPreset: 'all',
      };
      handleAddTable(config, payload.label);
    },
    [handleAddTable],
  );

  const handleUpdateTable = async (
    id: string,
    config: Omit<TableConfig, 'id' | 'label'>,
  ) => {
    // Find the current table config to check if label is custom
    const currentConfig = tableConfigs.find((tc) => tc.id === id);
    const currentLabel = currentConfig?.label;

    // Generate what the auto-generated label would be for the current config
    // Use generateTableName to match the format used by the component
    const currentAutoGeneratedLabel = currentConfig
      ? generateTableName(
          currentConfig.primaryXDimension ?? DimensionType.TIME,
          currentConfig.secondaryXDimension,
          currentConfig.primaryYDimension,
          currentConfig.secondaryYDimension,
          currentConfig.dimensionGroupingConfig,
        )
      : null;

    // If current label differs from auto-generated, it's custom - preserve it
    // Otherwise, regenerate for the new config
    const label =
      currentLabel &&
      currentAutoGeneratedLabel &&
      currentLabel !== currentAutoGeneratedLabel
        ? currentLabel
        : generateTableName(
            config.primaryXDimension ?? DimensionType.TIME,
            config.secondaryXDimension,
            config.primaryYDimension,
            config.secondaryYDimension,
            config.dimensionGroupingConfig,
          );

    await updateCustomView.mutateAsync({
      id,
      data: {
        config: config as Record<string, unknown>,
        title: label,
      },
    });
  };

  const handleUpdateTableLabel = async (id: string, label: string) => {
    await updateCustomView.mutateAsync({
      id,
      data: {
        title: label,
      },
    });
  };

  const handleDeleteTable = async (id: string) => {
    await deleteCustomView.mutateAsync({ id });
  };

  const handleReorderTables = async (newOrder: TableConfig[]) => {
    const orderedIds = newOrder.map((table) => table.id);
    await reorderCustomViews.mutateAsync({
      module: 'REBATES',
      section: 'ANALYTICS_TABLES',
      orderedIds,
    });
  };

  // TODO: Fetch all rebate rules for conditional/non-conditional breakdown
  // This requires either:
  // 1. A new API endpoint to get all rules across agreements
  // 2. A more complex hook that can handle multiple agreement rule fetches
  // For now, we'll keep rebateRules as undefined so the analytics page shows
  // placeholder cards for conditional/non-conditional breakdown

  if (
    isLoading ||
    isLoadingPreviousYear ||
    divisionsLoading ||
    customViewsLoading
  ) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Rebate Forecast</h1>
          </div>
          <div className="text-center py-12">
            <h2 className="text-lg text-muted-foreground">
              Loading forecast data...
            </h2>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  // Check if we have the minimum data needed to render the page structure
  const isInitialLoading = isLoading && !multiYearForecastData.currentYearData;
  const hasError = error && !multiYearForecastData.currentYearData;

  if (hasError) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Rebate Forecast</h1>
          </div>
          <div className="text-center py-12">
            <h2 className="text-lg text-red-600">
              Error loading forecast data
            </h2>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 w-full min-w-0 max-w-full overflow-hidden">
        <div className="mb-6 flex items-center justify-between min-w-0">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold">Rebate Forecast</h1>
            {multiYearForecastData.currentYearData?.forecastPeriod
              ?.description && (
              <p className="text-sm text-muted-foreground mt-2">
                {
                  multiYearForecastData.currentYearData.forecastPeriod
                    .description
                }
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {availableYears && availableYears.length > 0 && (
              <SingleSelect
                label="Year:"
                labelPosition="left"
                options={availableYears
                  .sort((a, b) => b - a)
                  .map((year) => ({
                    id: year.toString(),
                    label: year.toString(),
                    value: year,
                  }))}
                shouldAutoSort={false}
                value={
                  selectedYear !== null
                    ? {
                        id: selectedYear.toString(),
                        label: selectedYear.toString(),
                        value: selectedYear,
                      }
                    : null
                }
                onChange={(option) => {
                  const newYear = option
                    ? (option.value as number)
                    : currentYear;
                  handleYearChange(newYear);
                }}
                placeholder="Select year..."
                searchPlaceholder="Search years..."
                size="sm"
                allowClear={false}
              />
            )}

            {/* Admin-only bulk forecast reset buttons */}
            {userIsAdmin && agreementIds.length > 0 && (
              <>
                <div className="w-px h-6 bg-border mx-1" />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setResetForecastDialogOpen(true)}
                        className="gap-2"
                      >
                        <ArrowsClockwiseIcon className="h-4 w-4" />
                        Reset Forecast
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Reset forecast for all {agreementIds.length} agreements
                        (Admin)
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setResetWithReportedDialogOpen(true)}
                        className="gap-2 text-orange-600 hover:text-orange-700"
                      >
                        <ArrowsClockwiseIcon className="h-4 w-4" />
                        Reset + Reported
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Reset forecast and reported for all{' '}
                        {agreementIds.length} agreements (Admin)
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </div>

        {/* Forecast Chart with Status Cards */}
        <div className="pt-6 w-full max-w-full">
          <ForecastCard
            multiYearForecastData={transformedForecastData}
            showStatusCards
            compact
            rebateRules={
              transformedForecastData.currentYearData?.rules ?? undefined
            }
            isLoadingForecast={isLoading || isInitialLoading}
            isCumulative={isCumulative}
            ruleTagsMap={ruleTagsMapWithNames}
            tagNameToIdMap={tagNameToIdMap}
            visibleLines={visibleLines}
            primaryLine={primaryLine}
            onVisibleLinesChange={handleVisibleLinesChange}
            predictionSplitDay={predictionSplitDay}
            lastRealEstimate={
              lastRealEstimate
                ? {
                    daysFromStart: lastRealEstimate.daysFromStart,
                    totalRebateAmount: lastRealEstimate.totalRebateAmount,
                    date: lastRealEstimate.date,
                  }
                : null
            }
          />
        </div>

        {/* Custom Breakdown Tables */}
        <div className="mb-6 w-full max-w-full">
          <RebateAnalyticsTables
            tableConfigs={tableConfigs}
            onAddTable={handleAddTable}
            onUpdateTable={handleUpdateTable}
            onUpdateTableLabel={handleUpdateTableLabel}
            onDeleteTable={handleDeleteTable}
            onReorderTables={handleReorderTables}
            multiYearForecastData={transformedForecastData}
            isCumulative={isCumulative}
            primaryLine={primaryLine}
            supplierIdToName={supplierIdToName}
            divisionIdToName={divisionIdToName}
            ruleTagsMap={ruleTagsMap}
            agreementTagsMap={agreementTagsMap}
            supplierTagsMap={supplierTagsMap}
            ruleTagIdToName={ruleTagIdToName}
            agreementTagIdToName={agreementTagIdToName}
            supplierTagIdToName={supplierTagIdToName}
            ruleIdToName={ruleIdToName}
            agreementIdToName={agreementIdToName}
            creatorName={creatorName}
            activeFilterCount={
              (selectedRuleTagIds.size > 0 ? 1 : 0) +
              (selectedConditionalTypes.size < 2 ? 1 : 0) +
              (selectedAgreementTagIds.size > 0 ? 1 : 0) +
              (selectedSupplierTagIds.size > 0 ? 1 : 0) +
              (selectedDivisionTags.length > 0 ? 1 : 0) +
              (selectedSuppliers.length > 0 ? 1 : 0) +
              (selectedAgreements.length > 0 ? 1 : 0)
            }
            activeFilterNames={activeFilterNames}
          />
        </div>

        {/* Floating Controls FAB */}
        <ForecastControlsFAB
          isCumulative={isCumulative}
          onCumulativeChange={handleCumulativeChange}
          filterGroups={filterGroups}
          activeFilterCount={activeFilterCount}
          onClearAll={handleClearAllFilters}
          visibleLines={visibleLines}
          onVisibleLinesChange={handleVisibleLinesChange}
          primaryLine={primaryLine}
          onPrimaryLineChange={handlePrimaryLineChange}
          hasPreviousYearData={!!transformedForecastData.previousYearData}
          onReset={handleResetAll}
          isResetAvailable={isResetAvailable}
          selectedYear={selectedYear}
          availablePredictionAlgorithms={availablePredictionAlgorithms}
        />

        {/* AI Chat Assistant */}
        <ForegroundAgentChat
          moduleKey="rebates"
          currentPage="/rebates/analytics"
          onNavigate={(path, options) =>
            navigate({
              to: path,
              search: options?.search as Record<string, unknown>,
            })
          }
          onUpdateFilters={handleAIFilterUpdate}
          onCreateBreakdownTable={handleAICreateBreakdownTable}
        />

        {/* Admin-only: Bulk Reset Forecast Dialog */}
        <ConfirmDialog
          open={resetForecastDialogOpen}
          onClose={() => setResetForecastDialogOpen(false)}
          onConfirm={handleBulkResetForecast}
          title="Reset Forecast (Bulk)"
          message={`This will mark the forecast as stale for all ${agreementIds.length} agreement(s) in ${selectedYear} and trigger a full regeneration.\n\nAll estimates, predictions, and true-up calculations will be recalculated from scratch.\n\nReported values will be preserved (they won't be overwritten if they already exist).\n\nNote: Historical agreements will be automatically skipped.`}
          confirmText="Reset Forecast"
          loading={bulkResetForecastMutation.isPending}
          dangerous={false}
        />

        {/* Admin-only: Bulk Reset Forecast and Reported Dialog */}
        <ConfirmDialog
          open={resetWithReportedDialogOpen}
          onClose={() => setResetWithReportedDialogOpen(false)}
          onConfirm={handleBulkResetForecastAndReported}
          title="Reset Forecast and Reported (Bulk)"
          message={`WARNING: This will permanently delete all REPORTED breakdown values for all ${agreementIds.length} agreement(s) in ${selectedYear}.\n\nThis action:\n1. Deletes all frozen "reported" amounts across all agreements\n2. Marks forecasts as stale for all agreements\n3. Triggers a full regeneration\n\nNote: Historical agreements will be automatically skipped.\n\nUse this when reported values are incorrect and need to be recalculated from scratch.`}
          confirmText="Reset Forecast and Reported"
          loading={bulkResetWithReportedMutation.isPending}
          dangerous={true}
        />
      </div>
    </DashboardLayout>
  );
};

export default RebatesAnalyticsPage;
